import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { RequestHeader } from "@features/cx/shared/components/RequestHeader";
import { RequestSummaryCards } from "@features/cx/shared/components/RequestSummaryCards";
import { RequestQuickActions } from "@features/cx/shared/components/RequestQuickActions";
import { ActivitySidebar } from "@features/cx/shared/components/ActivitySidebar";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { ServiceOrdersTab } from "@features/cx/shared/components/tabs/ServiceOrdersTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import {
  CheckCircle,
  Clock,
  FileText,
  InfoIcon,
  MessageSquareIcon,
  Pause,
  Play,
  XCircle,
  DollarSign,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  useTransferRequestDetail,
  useUpdateTransferRequest,
} from "../../hooks";
import TransferDetailsTab from "./tabComponents/TransferDetailsTab";
import { formatTerritoryAddress } from "@shared/utils/address";
import { BillPaymentsTab } from "./tabComponents/BillPaymentTab";

export function TransferDetail() {
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const { data: serviceRequestData } = useTransferRequestDetail(
    Number(id),
    Number(remoteUtilityId),
  );

  const [currentStatus, setCurrentStatus] = useState<
    | "pending"
    | "in progress"
    | "completed"
    | "cancelled"
    | "draft"
    | "submitted"
    | "approved"
    | "rejected"
    | "on_hold"
  >("pending");

  // Update currentStatus when API data is loaded
  useEffect(() => {
    if (serviceRequestData?.result?.statusDisplay) {
      const statusFromAPI =
        serviceRequestData.result.statusDisplay.toLowerCase();
      setCurrentStatus(
        statusFromAPI as
        | "pending"
        | "in progress"
        | "completed"
        | "cancelled"
        | "draft"
        | "submitted"
        | "approved"
        | "rejected"
        | "on_hold",
      );
    }
  }, [serviceRequestData]);

  const { mutateAsync: updateServiceRequest } = useUpdateTransferRequest(
    Number(id),
  );

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const request = serviceRequestData?.result;
  console.log("Transfer Request Details:", request, request?.categoryDisplay,
    request?.subCategoryDisplay);
  // Status change handlers
  const handleAcknowledge = () => {
    // update the status in the database
    updateServiceRequest({
      status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
      actualAcknowledgeTime: new Date().toISOString(),
    });
    setCurrentStatus("in progress");
  };

  const handlePutOnHold = () => {
    // update the status in the database
    updateServiceRequest({
      status: SERVICE_REQUEST_STATUS.ON_HOLD,
    });
    setCurrentStatus("on_hold");
  };

  const handleResume = () => {
    // update the status in the database
    updateServiceRequest({
      status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
    });
    setCurrentStatus("in progress");
  };

  const handleResolveRequest = () => {
    // update the status in the database
    updateServiceRequest({
      status: SERVICE_REQUEST_STATUS.COMPLETED,
      closeDate: new Date().toISOString(),
    });
    setCurrentStatus("completed");
  };

  const handleRejectRequest = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateServiceRequest({
        status: SERVICE_REQUEST_STATUS.REJECTED,
        closeDate: new Date().toISOString(),
        closureRemark: reason || "Service request rejected by the consumer!",
      });
      setCurrentStatus("rejected");

      setRejectDialogOpen(false);
    } catch (error) {
    } finally {
      setIsRejecting(false);
    }
  };

  // Action buttons for RequestHeader
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.PENDING],
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) => request?.statusDisplay === "ON HOLD",
    },
  ];

  // Transform API data to match RequestHeader interface
  const transformedRequestForHeader = {
    id: request?.id.toString(),
    name: request?.utilitySupportRequest?.name || "NA",
    type: request?.utilitySupportRequest?.name || "NA",
    reason: request?.requestType,
    dateRequested: request?.createdDate,
    customerName:
      request?.consumer.firstName + " " + request?.consumer.lastName,
    accountNumber: request?.consumer.consumerNo,
    priority: request?.utilitySupportRequest?.extraData?.priorityLevel || 0,
    isVip: request?.consumer.isVip,
    status: currentStatus,
    sla: {
      status: request?.slaSummary.slaStatus,
      dueDate: request?.slaSummary.slaDueDate,
      remaining: request?.slaSummary.timeRemaining,
    },
    priorityDisplay: request?.priorityLevelDisplay,
    requestNo:request?.requestNo,
  };

  // Transform API data to match RequestSummaryCards interface
  const transformedRequestForSummary = {
    id: request?.id.toString(),
    requestNo: request?.requestNo,
    dateRequested: request?.createdDate,
    requestedBy: request?.createdUserRemoteName,
    assignedTo: request?.assignedTo || "Unassigned",
    lastUpdatedOn: request?.lastModifiedDate,
    lastUpdatedBy: request?.lastModifiedUserRemoteName,
    additionalData: request?.additionalData,
    resolvedOn:request?.closeDate,
    resolvedBy:request?.resolvedBy,
    customer: {
      consumerNo: request?.consumer.consumerNo || "",
      id: request?.consumer.id?.toString() || "",
      firstName: request?.consumer.firstName || "",
      lastName: request?.consumer.lastName || "",
      phone: request?.consumer.contactNumber,
      email: request?.consumer.email,
      categoryDisplay: request?.categoryDisplay,
      subCategoryDisplay: request?.subCategoryDisplay,
      serviceAddress:
        formatTerritoryAddress(
          { service: request?.consumer.consumerAddress.service.hierarchyNames },
          "service",
        ) || "No address configured",
      billingAddress:
        formatTerritoryAddress(
          { billing: request?.consumer.consumerAddress.billing.hierarchyNames },
          "billing",
        ) || "No address configured",
      ownershipDisplay: request?.consumerOwnershipLabel,
      masterAccountNo: request?.consumer?.ownershipDetails?.approvalDetails?.masterAccountNo || "NA",
    },
  };

  // Simplified request for QuickActions
  const requestForQuickActions = {
    id: request?.id.toString() || "",
    requestNo: request?.requestNo || "",
    customer: {
      id: request?.consumer.id?.toString() || "NA",
      firstName: request?.consumer.firstName || "NA",
      lastName: request?.consumer.lastName || "NA",
      phone: request?.consumer.contactNumber || "NA",
      email: request?.consumer.email || "NA",
      consumerNo: request?.consumer.consumerNo || "NA",
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Content Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Left Column (3/4): Header, Summary, and Tabs */}
        <div className="lg:col-span-3 space-y-6">
          <RequestHeader
            request={transformedRequestForHeader}
            currentStatus={currentStatus.toLowerCase()}
            actionButtons={actionButtons}
            action={"Transfer"}
          >
            <RequestSummaryCards
              request={transformedRequestForSummary}
            />

            {/* Configurable Tabs Section */}
            <div className="mt-6 pt-4">
              <Tabs
                className="w-full"
                tabsListClassName={`grid w-full mb-6 ${
                  !request?.additionalData?.transferOutstandingBalance
                    ? "grid-cols-6"
                    : "grid-cols-5"
                }`}
                defaultValue="details"
                urlMapping={{
                  details: "details",
                  outstandingBalance: "outstanding-balance",
                  communication: "communication",
                  serviceOrders: "service-orders",
                }}
                tabComponents={{
                  details: {
                    label: "Details",
                    icon: <InfoIcon className="h-4 w-4" />,
                    component: (
                      <TransferDetailsTab
                        request={{
                          id: request?.id,
                          consumer: request?.consumer,
                          remoteUtilityId: Number(remoteUtilityId),
                          requestNo: request?.requestNo,
                          status: request?.status,
                          requestType: request?.requestType,
                          createdDate: request?.createdDate,
                          createdUserRemoteName: request?.createdUserRemoteName,
                          utilitySupportRequest:
                            request?.utilitySupportRequest as unknown as number,
                          statusDisplay: request?.statusDisplay,
                          requestDate: request?.createdDate,
                          acknowledgementTimeAchieved:
                            request?.acknowledgementTimeAchieved,
                          resolutionTimeAchieved: request?.resolutionTimeAchieved,
                          additionalData: request?.additionalData,
                          relation: request?.consumerRelationshipDisplay,
                          financialDetails: request?.financialDetails,
                          priorityLevelDisplay: request?.priorityLevelDisplay,
                          consumerRelationshipDisplay:request?.consumerRelationshipDisplay,
                        }}
                        onResolve={handleResolveRequest}
                        onReject={handleRejectRequest}
                      />
                    ),
                  },
                  ...(!request?.additionalData?.transferOutstandingBalance ? {
                    outstandingBalance: {
                      label: "Final Bill & Payments",
                      icon: <DollarSign className="h-4 w-4" />,
                      component: (
                        <BillPaymentsTab 
                          consumerId={Number(request?.consumer.id)}
                          requestId={Number(request?.id)}
                          premise={request?.consumer?.consumerAddress?.billing?.hierarchyNames?.premise || ""}
                          additionalData={request?.additionalData}
                          requestStatus={request?.statusDisplay}
                        />
                      ),
                    }
                  } : {}),
                  serviceOrders: {
                    label: "Service Orders",
                    icon: <CheckCircle className="h-4 w-4" />,
                    component: (
                      <ServiceOrdersTab
                        consumerAccountNumber={request?.consumer.consumerNo || ""}
                        consumerId={request?.consumer.id?.toString() || ""}
                        consumerName={
                          request?.consumer.firstName +
                          " " +
                          request?.consumer.lastName
                        }
                        requestId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                        currentStatus={request?.statusDisplay}
                      />
                    ),
                  },
                  communication: {
                    label: "Communication",
                    icon: <MessageSquareIcon className="h-4 w-4" />,
                    component: (
                      <RequestCommunications
                        entityId={request?.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                        email={request?.consumer.email}
                        phone={request?.consumer.contactNumber || ""}
                        action="consumer_support_request"
                      />
                    ),
                  },
                  notes: {
                    label: "Notes",
                    icon: <FileText className="h-4 w-4" />,
                    component: (
                      <NotesTab
                        source={null}
                        requestId={request?.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                      />
                    ),
                  },
                  timeLine: {
                    label: "Time Line",
                    icon: <Clock className="h-4 w-4" />,
                    component: (
                      <TimelineTab
                        entityId={request?.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                        actionType="transfer"
                      />
                    ),
                  },
                }}
              />
            </div>
          </RequestHeader>
        </div>

        {/* Right Column (1/4): Quick Actions and Activity Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <RequestQuickActions 
            request={requestForQuickActions}
            currentStatus={currentStatus}
            actionButtons={actionButtons}
            handleResolveRequest={handleResolveRequest}
            handleRejectRequest={handleRejectRequest}
          />
          <ActivitySidebar
            entityId={request?.id?.toString() || ""}
            remoteUtilityId={Number(remoteUtilityId) || 0}
            actionType="transfer"
          />
        </div>
      </div>

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Service Request"
        description="Please provide a reason for rejecting this service request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
}
